import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  updateProfile,
  type ActionCodeSettings,
  type AuthProvider,
  type User,
} from "firebase/auth";
import { FirebaseError } from "firebase/app";
import { getFirebaseAuth } from "@/config/firebase";
import type { UserProfile, UserProfileUpdate } from "@/types/user";
import type { UsageLogPayload } from "@/types/usage";
import {
  ProfileRequestError,
  deleteUserAccount,
  deleteUserAvatar,
  fetchCurrentUserProfile,
  logUserUsage,
  patchCurrentUserProfile,
  replaceUserSettings,
  uploadUserAvatar,
  updateLastActive,
} from "@/services/user-profile";

const auth = getFirebaseAuth();

type AuthProviderId = "google";

interface AuthContextValue {
  user: User | null;
  profile: UserProfile | null;
  isInitializing: boolean;
  isActionPending: boolean;
  isProfileRefreshing: boolean;
  isProfileMutating: boolean;
  authError: string | null;
  profileError: string | null;
  loginWithEmail: (email: string, password: string) => Promise<void>;
  registerWithEmail: (options: { email: string; password: string; displayName?: string }) => Promise<void>;
  loginWithProvider: (providerId: AuthProviderId) => Promise<void>;
  logout: () => Promise<void>;
  deleteAccount: () => Promise<void>;
  resendVerificationEmail: () => Promise<void>;
  sendPasswordReset: (email: string) => Promise<void>;
  clearError: () => void;
  refreshProfile: () => Promise<void>;
  updateProfile: (update: UserProfileUpdate) => Promise<UserProfile>;
  replaceSettings: (settings: Record<string, unknown>) => Promise<UserProfile>;
  uploadAvatar: (file: File) => Promise<UserProfile>;
  deleteAvatar: () => Promise<UserProfile>;
  logUsage: (usage: UsageLogPayload) => Promise<UserProfile | null>;
  updateUserLastActive: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function mapAuthError(error: unknown): string {
  if (error instanceof FirebaseError) {
    switch (error.code) {
      case "auth/popup-closed-by-user":
      case "auth/cancelled-popup-request":
        return "Sign in canceled. Please try again.";
      case "auth/email-already-in-use":
        return "This email is already in use.";
      case "auth/invalid-credential":
      case "auth/invalid-email":
        return "The provided credentials are invalid.";
      case "auth/wrong-password":
        return "Incorrect email or password.";
      case "auth/user-not-found":
        return "No account found with that email.";
      case "auth/popup-blocked":
        return "The sign-in popup was blocked by the browser.";
      default:
        return error.message ?? "An unexpected authentication error occurred.";
    }
  }

  if (error instanceof Error) {
    return error.message;
  }

  return "An unexpected authentication error occurred.";
}

function mapProfileError(error: unknown): string {
  if (error instanceof ProfileRequestError) {
    if (error.status === 410) {
      return "Your account has been deleted. Sign in again or contact support if this was unexpected.";
    }
    if (error.status === 401) {
      return "Your session has expired. Please sign in again.";
    }
    if (error.message) {
      return error.message;
    }
  }

  if (error instanceof Error && error.message) {
    const message = error.message;

    if (/not\s*found/i.test(message) || /\b404\b/.test(message)) {
      return "Account service returned 404. Start the Python API (`uvicorn app.main:app --reload --port 8000`) or update PYAPI_TARGET if it runs elsewhere.";
    }

    if (/failed to fetch/i.test(message) || /network/i.test(message)) {
      return "Unable to reach the account service. Verify the Python API is running locally on http://127.0.0.1:8000.";
    }

    return message;
  }

  return "Unable to load account profile.";
}

function createProvider(providerId: AuthProviderId): AuthProvider {
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: "select_account" });
  return provider;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const [isActionPending, setIsActionPending] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [isProfileRefreshing, setIsProfileRefreshing] = useState(false);
  const [isProfileMutating, setIsProfileMutating] = useState(false);

  const hydrateProfile = useCallback(
    async (
      currentUser: User,
      options: { markInitializing?: boolean; isActive?: () => boolean } = {}
    ): Promise<UserProfile> => {
      const { markInitializing = false, isActive } = options;

      if (markInitializing) {
        setIsInitializing(true);
      }

      if (!isActive || isActive()) {
        setIsProfileRefreshing(true);
        setProfileError(null);
      }

      try {
        const data = await fetchCurrentUserProfile(currentUser);
        if (isActive && !isActive()) {
          return data;
        }
        setProfile(data);
        return data;
      } catch (error) {
        const shouldSignOut =
          error instanceof ProfileRequestError && (error.status === 410 || error.status === 401);
        if (shouldSignOut) {
          try {
            await signOut(auth);
          } catch (signOutError) {
            console.warn("Failed to sign out after profile error", signOutError);
          }
        }

        const message = mapProfileError(error);
        if (!isActive || isActive()) {
          console.error("Failed to load user profile", error);
          setProfile(null);
          setProfileError(message);
        }
        throw error;
      } finally {
        if (!isActive || isActive()) {
          setIsProfileRefreshing(false);
          if (markInitializing) {
            setIsInitializing(false);
          }
        }
      }
    },
    []
  );

  useEffect(() => {
    let active = true;
    const unsubscribe = onAuthStateChanged(auth, (nextUser) => {
      if (!active) {
        return;
      }

      setUser(nextUser);

      if (!nextUser) {
        setProfile(null);
        setProfileError(null);
        setIsInitializing(false);
        return;
      }

      void hydrateProfile(nextUser, { markInitializing: true, isActive: () => active }).catch(() => {
        /* handled in hydrateProfile */
      });
    });

    return () => {
      active = false;
      unsubscribe();
    };
  }, [hydrateProfile]);

  const clearError = useCallback(() => {
    setAuthError(null);
    setProfileError(null);
  }, []);

  const handleAuthAction = useCallback(async (action: () => Promise<void>) => {
    setIsActionPending(true);
    setAuthError(null);

    try {
      await action();
    } catch (error) {
      setAuthError(mapAuthError(error));
      throw error;
    } finally {
      setIsActionPending(false);
    }
  }, []);

  const runProfileMutation = useCallback(
    async (mutation: (currentUser: User) => Promise<UserProfile>) => {
      const currentUser = auth.currentUser ?? user;

      if (!currentUser) {
        throw new Error("No authenticated user available");
      }

      setIsProfileMutating(true);
      setProfileError(null);

      try {
        const result = await mutation(currentUser);
        setProfile(result);
        return result;
      } catch (error) {
        const shouldSignOut =
          error instanceof ProfileRequestError && (error.status === 410 || error.status === 401);
        if (shouldSignOut) {
          try {
            await signOut(auth);
          } catch (signOutError) {
            console.warn("Failed to sign out after profile mutation error", signOutError);
          }
          setProfile(null);
        }

        const message = mapProfileError(error);
        setProfileError(message);
        throw error;
      } finally {
        setIsProfileMutating(false);
      }
    },
    [user]
  );

  const loginWithEmail = useCallback(
    async (email: string, password: string) => {
      await handleAuthAction(async () => {
        await signInWithEmailAndPassword(auth, email, password);
      });
    },
    [handleAuthAction]
  );

  const registerWithEmail = useCallback(
    async ({ email, password, displayName }: { email: string; password: string; displayName?: string }) => {
      await handleAuthAction(async () => {
        const credential = await createUserWithEmailAndPassword(auth, email, password);

        if (displayName) {
          await updateProfile(credential.user, { displayName });
        }

        // Send email verification
        const actionCodeSettings: ActionCodeSettings = {
          url: window.location.origin,
          handleCodeInApp: false,
        };
        
        try {
          await sendEmailVerification(credential.user, actionCodeSettings);
        } catch (error) {
          console.error("Failed to send verification email:", error);
          // Don't throw - allow registration to complete even if email fails
        }
      });
    },
    [handleAuthAction]
  );

  const loginWithProvider = useCallback(
    async (providerId: AuthProviderId) => {
      await handleAuthAction(async () => {
        const provider = createProvider(providerId);
        await signInWithPopup(auth, provider);
      });
    },
    [handleAuthAction]
  );

  const logout = useCallback(async () => {
    await handleAuthAction(async () => {
      await signOut(auth);
    });
  }, [handleAuthAction]);

  const deleteAccount = useCallback(async () => {
    await handleAuthAction(async () => {
      const currentUser = auth.currentUser ?? user;

      if (!currentUser) {
        throw new Error("No authenticated user available");
      }

      await deleteUserAccount(currentUser);
      setProfile(null);
      setProfileError(null);
      await signOut(auth);
    });
  }, [handleAuthAction, user]);

  const resendVerificationEmail = useCallback(async () => {
    await handleAuthAction(async () => {
      const currentUser = auth.currentUser ?? user;

      if (!currentUser) {
        throw new Error("No authenticated user available");
      }

      if (currentUser.emailVerified) {
        throw new Error("Email is already verified");
      }

      const actionCodeSettings: ActionCodeSettings = {
        url: window.location.origin,
        handleCodeInApp: false,
      };

      await sendEmailVerification(currentUser, actionCodeSettings);
    });
  }, [handleAuthAction, user]);

  const sendPasswordReset = useCallback(
    async (email: string) => {
      await handleAuthAction(async () => {
        const actionCodeSettings: ActionCodeSettings = {
          url: window.location.origin,
          handleCodeInApp: false,
        };
        await sendPasswordResetEmail(auth, email, actionCodeSettings);
      });
    },
    [handleAuthAction]
  );

  const refreshProfile = useCallback(async () => {
    const currentUser = auth.currentUser ?? user;

    if (!currentUser) {
      setProfile(null);
      setProfileError(null);
      return;
    }

    await hydrateProfile(currentUser);
  }, [hydrateProfile, user]);

  const updateProfileMutation = useCallback(
    async (update: UserProfileUpdate) => runProfileMutation((current) => patchCurrentUserProfile(current, update)),
    [runProfileMutation]
  );

  const replaceSettings = useCallback(
    async (settings: Record<string, unknown>) => runProfileMutation((current) => replaceUserSettings(current, settings)),
    [runProfileMutation]
  );

  const uploadAvatar = useCallback(
    async (file: File) => runProfileMutation((current) => uploadUserAvatar(current, file)),
    [runProfileMutation]
  );

  const deleteAvatar = useCallback(
    async () => runProfileMutation((current) => deleteUserAvatar(current)),
    [runProfileMutation]
  );

  const logUsage = useCallback(
    async (usage: UsageLogPayload) => {
      const currentUser = auth.currentUser ?? user;

      if (!currentUser) {
        return null;
      }

      try {
        const result = await logUserUsage(currentUser, usage);
        setProfile(result);
        return result;
      } catch (error) {
        console.warn("Failed to log usage", error);
        return null;
      }
    },
    [user]
  );

  const updateUserLastActive = useCallback(async () => {
    const currentUser = auth.currentUser ?? user;

    if (!currentUser) {
      return;
    }

    try {
      const result = await updateLastActive(currentUser);
      setProfile(result);
    } catch (error) {
      console.warn("Failed to update last active timestamp", error);
    }
  }, [user]);
  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      profile,
      isInitializing,
      isActionPending,
      isProfileRefreshing,
      isProfileMutating,
      authError,
      profileError,
      loginWithEmail,
      registerWithEmail,
      loginWithProvider,
      logout,
      deleteAccount,
      resendVerificationEmail,
      sendPasswordReset,
      clearError,
      refreshProfile,
      updateProfile: updateProfileMutation,
      replaceSettings,
      uploadAvatar,
      deleteAvatar,
      logUsage,
      updateUserLastActive,
    }),
    [
      authError,
      clearError,
      deleteAccount,
      deleteAvatar,
      isActionPending,
      isInitializing,
      isProfileMutating,
      isProfileRefreshing,
      loginWithEmail,
      loginWithProvider,
      logUsage,
      logout,
      profile,
      profileError,
      refreshProfile,
      registerWithEmail,
      replaceSettings,
      resendVerificationEmail,
      sendPasswordReset,
      updateProfileMutation,
      uploadAvatar,
      updateUserLastActive,
      user,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuthContext(): AuthContextValue {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuthContext must be used within an AuthProvider");
  }

  return context;
}
